import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Box } from "@mui/material";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import Typography from "@mui/material/Typography";
import { CustomDialog } from "../Reusables/CustomerDialog";
import {
  CUSTOMER_FIELDS,
  CUSTOMER_MANAGEMENT_TABLE_HEADINGS,
  DASHBOARD_CUSTOMER_CONSTANTS,
  format24HourToAmPm,
  getCustomerDetail,
} from "../../constants/CustomerConstants";
import { DeleteDialog } from "../Modals/DeleteDialog";
import { useToastContext } from "../../context/toast";
import { deleteCustomerById, fetchManageCustomer, searchCustomerContactDetails } from "../../api/dashboardCustomers.Service";
import { useTheme } from "@mui/material/styles";
import ReusableSearch from "../Reusables/ReusableSearch";
import IconLabelButtons from "../Reusables/ReusableButton";
import ReusablePaginationTable from "../Reusables/ReusablePaginationTable";
import { validateEmptyOrNull } from "../../utilities/libs";

const DashboardCustomers = ({ accountInfo }) => {
  const [customersList, setCustomersList] = useState([]);
  const [customerData, setCustomerData] = useState(getCustomerDetail());
  const [searchQuery, setSearchQuery] = useState("");
  const [addEditCustomerDialog, setAddEditCustomerDialog] = useState(false);
  const [isEditDialog, setIsEditDialog] = useState(false);
  const [deleteCustomerDialog, setDeteleCustomerDialog] = useState(false);
  const [deleteCustomerID, setDeleteCustomerID] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false)
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [fieldErrors, setFieldErrors] = useState({
    customerId: false,
    emailAddress: false,
    recoveryTime: false,
    recoveryDay: false,
    startReceivingHours: false,
    endRecevingHours: false,
  });
  const pageSize = 100;
  const { showErrorToast, showSuccessToast } = useToastContext();
  const theme = useTheme();

  const onFetchCustomers = useCallback(async (isInitialFetch = false) => {
    if (!hasMore && !isInitialFetch) return;
    setLoading(true);

    if (isInitialFetch) {
      setPage(1);
    }

    try {
      const response = await fetchManageCustomer(isInitialFetch ? 1 : page, pageSize);
      const { isSuccessful, data, message, totalCount, hasMore: moreData } = response;

      if (!isSuccessful) {
        setError(true)
        setLoading(false);
        showErrorToast(message || "Failed to load Customers details");
        setHasMore(false);
        setTotalCount(totalCount)
        return;
      }

      if (isInitialFetch) {
        setError(false)
        setCustomersList(data);
        setPage(2); // Next page
        setHasMore(moreData ?? data.length > 0);
        setTotalCount(totalCount)
      } else {
        setError(false)
        setCustomersList((prev) => [...prev, ...data]);
        setPage((prevPage) => prevPage + 1);
        setHasMore(moreData ?? data.length > 0);
        setTotalCount(totalCount)
      }

    } catch (err) {
      showErrorToast("Something went wrong while fetching customers");
      setError(true)
      setHasMore(false);
      setTotalCount(0)
    } finally {
      setLoading(false);
    }
  }, [page, hasMore]);

  const handleClose = () => {
    setCustomerData({
      ...getCustomerDetail(),
    });
    setAddEditCustomerDialog(false);
    setFieldErrors({
      customerId: false,
      emailAddress: false,
      recoveryTime: false,
      recoveryDay: false,
      startReceivingHours: false,
      endRecevingHours: false,
    });
  };

  const handleResetPaginationTable = () => {

    // Reset scroll position of the scroll container to top
    const scrollContainer = document.getElementById('scrollableDiv');
    if (scrollContainer) {
      scrollContainer.scrollTop = 0;
    }

    setPage(1);
    setHasMore(false);
    setLoading(true);
  };

  const handleSearchQueryChange = (event) => {
    setSearchQuery(event.target.value);
  };

  const handleSearchClear = async () => {

    handleResetPaginationTable()

    // Reset search query and customer list
    setSearchQuery('');
    setCustomersList([]);
    setError(false);

    await onFetchCustomers(true);
  };

  const handleSearch = async () => {
    setError(false)
    setLoading(true);

    if (validateEmptyOrNull(searchQuery)) {
      setLoading(false);
      setCustomersList([]);
      setError(true);
      return
    }

    const response = await searchCustomerContactDetails(searchQuery);
    const { isSuccessful, data, message } = response;
    if (!isSuccessful) {
      setError(true)
      // showErrorToast(message);
      setLoading(false);
      setCustomersList(data);
      setError(true);
      return;
    }

    setCustomersList(data);
    setLoading(false);
    setPage(1);
    setHasMore(false);
    setTotalCount(data.length);
  };

  const handleEditClick = (data) => {
    setIsEditDialog(true);
    setCustomerData({
      ...getCustomerDetail(),
      ...data,
    });
    setAddEditCustomerDialog(true);
  };

  const handleDeleteClick = (item) => {
    setIsEditDialog(false);
    setDeleteCustomerID(item.customerId);
    setDeteleCustomerDialog(true);
  };

  const handleCustomerDeleteApi = async () => {
    const response = await deleteCustomerById(deleteCustomerID);
    const { isSuccessful, message } = response;

    if (!isSuccessful) {
      showErrorToast(message);
      setDeteleCustomerDialog(false);
      return;
    }

    setSearchQuery('');
    // Reset pagination state
    handleResetPaginationTable()
    onFetchCustomers(true);
    showSuccessToast(message);
    setDeteleCustomerDialog(false);
  };

  const handleAddNewClick = () => {
    setIsEditDialog(false);
    setCustomerData({
      ...getCustomerDetail(),
    });
    setFieldErrors({
      customerId: false,
      emailAddress: false,
      recoveryTime: false,
      recoveryDay: false,
      startReceivingHours: false,
      endRecevingHours: false,
    });
    setAddEditCustomerDialog(true);
  };

  function formatCustomersList(customersList) {
    return customersList.map(({ customerId, phoneNumber, warehouse, emailAddress,
      recoveryTime, recoveryDay, startReceivingHours, endRecevingHours }) => ({
        customerId,
        phoneNumber,
        warehouse,
        emailAddress,
        recoveryTime,
        autoRecoveryWindow: (!validateEmptyOrNull(recoveryTime) && !validateEmptyOrNull(recoveryDay)) ?
          `${format24HourToAmPm(recoveryTime)}, ${recoveryDay}` : '',
        recoveryDay,
        startReceivingHours,
        endRecevingHours,
        hoursOfReceiving: (!validateEmptyOrNull(startReceivingHours) && !validateEmptyOrNull(endRecevingHours)) ?
          `${format24HourToAmPm(startReceivingHours)} - ${format24HourToAmPm(endRecevingHours)}` : ''
      }));
  }

  const actionFunctions = (iconType, item) => {
    switch (iconType) {
      case "edit":
        handleEditClick(item);
        break;
      case "delete":
        handleDeleteClick(item);
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    onFetchCustomers(true);
    setError(false)
  }, []);

  const formattedCustomers = React.useMemo(() => formatCustomersList(customersList), [customersList])

  return (
    <Box sx={{ padding: "0px 20px", margin: "0px" }}>
      <CustomDialog
        customerData={customerData}
        setCustomerData={setCustomerData}
        open={addEditCustomerDialog}
        onClose={handleClose}
        fields={CUSTOMER_FIELDS}
        isEditDialog={isEditDialog}
        onFetchCustomers={onFetchCustomers}
        loggedInUser={accountInfo?.account?.name}
        setCustomersList={setCustomersList}
        handleResetPaginationTable={handleResetPaginationTable}
        setSearchQuery={setSearchQuery}
        fieldErrors={fieldErrors}
        setFieldErrors={setFieldErrors}
      />
      <DeleteDialog
        row_id={deleteCustomerID}
        label={DASHBOARD_CUSTOMER_CONSTANTS.ROW_ITEM}
        deleteDialog={deleteCustomerDialog}
        setDeleteDialog={setDeteleCustomerDialog}
        handleDeleteClick={handleCustomerDeleteApi}
      />
      <Box
        sx={{
          padding: "0px",
          margin: "0px",
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
        }}
      >
        <ReusableSearch
          searchQuery={searchQuery}
          handleSearchQueryChange={handleSearchQueryChange}
          defaultSearchTypePlaceholder={DASHBOARD_CUSTOMER_CONSTANTS.DEFAULT_SEARCH_PLACEHOLDER}
          handleSearch={handleSearch}
          handleClear={handleSearchClear}
          searchIconPosition={DASHBOARD_CUSTOMER_CONSTANTS.END}
          clearIconPosition={DASHBOARD_CUSTOMER_CONSTANTS.START}
        />
        <IconLabelButtons
          btnVariant='contained'
          isIcon={true}
          iconPosition={DASHBOARD_CUSTOMER_CONSTANTS.END}
          Icon={AddOutlinedIcon}
          btnLabel={DASHBOARD_CUSTOMER_CONSTANTS.ADD_NEW_BUTTON_LABEL}
          btnAction={handleAddNewClick}
        />
      </Box>

      <Box sx={{ paddingTop: "20px", margin: "0px", textAlign: "center" }}>
        {customersList && customersList.length === 0 && error ? (
          <Box
            sx={{
              borderRadius: "6px",
              textAlign: "center",
              boxShadow: 'rgba(0, 0, 0, 0.2) 0px 2px 1px -1px, rgba(0, 0, 0, 0.14) 0px 1px 1px 0px, rgba(0, 0, 0, 0.12) 0px 1px 3px 0px',
            }}
          >
            <Typography
              sx={{
                padding: "10px",
                fontSize: "20px",
                fontWeight: "medium",
                textAlign: "center",
                color: theme.palette.text.primary,
              }}
            >
              No Customer Details Found
            </Typography>
          </Box>
        ) : (
          <ReusablePaginationTable
            headings={CUSTOMER_MANAGEMENT_TABLE_HEADINGS}
            rows={formattedCustomers}
            handleIconClick={actionFunctions}
            fetchData={onFetchCustomers}
            hasMore={hasMore}
            totalCount={totalCount}
          />
        )}
      </Box>
    </Box>
  );
};

export default DashboardCustomers;
